import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  CurrencyDollar, ShoppingCart, Receipt, ChartLineUp,
  MagnifyingGlass, X, CaretLeft, CaretRight, Funnel,
} from "@phosphor-icons/react";
import { useTheme } from "../../utils/context/ThemeContext";
import { PageHeader, Button, DateRange } from "../../components/ui";

/* -------- utils -------- */
const currency = (v) =>
  new Intl.NumberFormat("fr-MA", { style: "currency", currency: "MAD", maximumFractionDigits: 0 }).format(v || 0);

const shortNumber = (v) => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return `${v ?? 0}`;
};

const pct = (v) => (v == null || Number.isNaN(v) ? "–" : `${Number(v).toFixed(2)}%`);

/* -------- presentational -------- */
const DeltaChip = ({ value }) => {
  const isNum = typeof value === "number" && !Number.isNaN(value);
  const positive = isNum ? value >= 0 : true;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        positive
          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          : "bg-red-500/10 text-red-600 dark:text-red-400"
      }`}
    >
      {isNum ? (positive ? "▲" : "▼") : "—"} {isNum ? `${Math.abs(value).toFixed(1)}%` : ""}
    </span>
  );
};

const KpiCard = ({ label, value, delta, icon: Icon, hint }) => (
  <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
    <div className="flex items-center justify-between">
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-accent-soft text-accent">
        <Icon size={20} weight="bold" />
      </span>
      {delta !== undefined && <DeltaChip value={delta} />}
    </div>
    <div className="mt-4 text-2xl font-semibold tracking-tight text-ink">{value}</div>
    <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-muted">{label}</div>
    {hint && <div className="mt-0.5 text-[11px] text-faint">{hint}</div>}
  </div>
);

const ChartCard = ({ title, subtitle, children }) => (
  <div className="rounded-2xl border border-line bg-surface shadow-card">
    <div className="border-b border-line px-5 py-4">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {subtitle && <p className="mt-0.5 text-xs text-muted">{subtitle}</p>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const MoneyTooltip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 shadow-pop">
      <div className="text-[11px] text-muted">{label}</div>
      <div className="text-sm font-semibold text-ink">{currency(payload[0].value)}</div>
    </div>
  ) : null;

const NumberTooltip = ({ active, payload, label }) =>
  active && payload?.length ? (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 shadow-pop">
      <div className="text-[11px] text-muted">{label}</div>
      <div className="text-sm font-semibold text-ink">{shortNumber(payload[0].value)} units</div>
    </div>
  ) : null;

/* -------- main -------- */
const Dashboard = () => {
  const { theme } = useTheme();

  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    conversionRate: null,
    deltas: { totalRevenue: 0, totalOrders: 0, averageOrderValue: 0 },
  });
  const [revenueData, setRevenueData] = useState([]);
  const [productData, setProductData] = useState([]);

  /* live chart colors from design tokens (re-read on theme change) */
  const [c, setC] = useState({ accent: "#EA580C", line: "#e9e9ec", muted: "#6e6e78", surface: "#ffffff" });
  useEffect(() => {
    const cs = getComputedStyle(document.documentElement);
    const rgb = (n) => {
      const v = cs.getPropertyValue(n).trim();
      return v ? `rgb(${v.replace(/\s+/g, ", ")})` : undefined;
    };
    setC({
      accent: rgb("--c-accent") || "#EA580C",
      line: rgb("--c-line") || "#e9e9ec",
      muted: rgb("--c-muted") || "#6e6e78",
      surface: rgb("--c-surface") || "#ffffff",
    });
  }, [theme]);

  /* filters */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const defaultFrom = new Date(today.getFullYear(), today.getMonth() - 6, 1);
  const [from, setFrom] = useState(iso(defaultFrom));
  const [to, setTo] = useState(iso(today));
  const [selectedProduct, setSelectedProduct] = useState(null);

  /* product search */
  const [productSearch, setProductSearch] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodPage, setProdPage] = useState(1);
  const [prodTotalPages, setProdTotalPages] = useState(1);
  const searchDebounce = useRef(null);

  const getProducts = async (page = 1, searchValue = "") => {
    try {
      setProdLoading(true);
      const response = await axiosInstance.post(`/products/search?page=${page}&per_page=10`, { searchValue });
      const responseData = response.data;
      if (responseData) {
        setProductOptions((responseData.data || []).map((p) => ({ id: p.id, name: p.name || p.reference || `#${p.id}` })));
        setProdPage(responseData.current_page || 1);
        setProdTotalPages(responseData.last_page || 1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProdLoading(false);
    }
  };

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => getProducts(1, productSearch.trim()), 350);
    return () => clearTimeout(searchDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearch]);

  useEffect(() => {
    getProducts(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const query = useMemo(
    () => ({ months: 7, top_products: 6, from, to, product_id: selectedProduct?.id || undefined }),
    [from, to, selectedProduct]
  );

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/stats/dashboard", { params: query });
      const data = res?.data || {};
      setKpis({
        totalRevenue: data?.kpis?.totalRevenue ?? 0,
        totalOrders: data?.kpis?.totalOrders ?? 0,
        averageOrderValue: data?.kpis?.averageOrderValue ?? 0,
        conversionRate: data?.kpis?.conversionRate ?? null,
        deltas: {
          totalRevenue: data?.kpis?.deltas?.totalRevenue ?? 0,
          totalOrders: data?.kpis?.deltas?.totalOrders ?? 0,
          averageOrderValue: data?.kpis?.deltas?.averageOrderValue ?? 0,
        },
      });
      setRevenueData(Array.isArray(data?.revenueData) ? data.revenueData : []);
      setProductData(Array.isArray(data?.productData) ? data.productData : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, selectedProduct]);

  const rangeSummary = `${from} → ${to}`;
  const productSummary = selectedProduct ? selectedProduct.name : "All products";

  const axisTick = { fontSize: 12, fill: c.muted };

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Dashboard"
        subtitle={loading ? "Loading analytics…" : "Your store performance at a glance."}
        actions={
          <Button
            variant="outline"
            icon={Funnel}
            onClick={() => setFiltersOpen((v) => !v)}
          >
            {filtersOpen ? "Hide filters" : "Filters"}
          </Button>
        }
      />

      {/* Filters */}
      {filtersOpen ? (
        <div className="mb-6 rounded-2xl border border-line bg-surface p-5 shadow-card">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1">
              <DateRange
                label="Date range"
                value={{ from, to }}
                onChange={({ from: f, to: t }) => {
                  setFrom(f);
                  setTo(t);
                }}
              />
            </div>

            <div className="flex-1">
              <label className="mb-1.5 block text-sm font-medium text-ink/90">Filter by product</label>
              {selectedProduct ? (
                <div className="flex h-11 items-center justify-between rounded-xl border border-line bg-surface-2 px-3.5 text-sm">
                  <span className="truncate text-ink">{selectedProduct.name}</span>
                  <button onClick={() => setSelectedProduct(null)} className="ml-3 grid h-7 w-7 place-items-center rounded-lg text-muted hover:bg-surface hover:text-ink" title="Clear">
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex h-11 items-center rounded-xl border border-line bg-surface-2 px-3.5">
                    <MagnifyingGlass size={16} className="text-faint" />
                    <input
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search products…"
                      className="ml-2 w-full bg-transparent text-sm text-ink outline-none placeholder:text-faint"
                    />
                  </div>
                  {productSearch.length > 0 && (
                    <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-line bg-surface shadow-pop">
                      {prodLoading ? (
                        <div className="px-3 py-2 text-sm text-muted">Searching…</div>
                      ) : productOptions.length ? (
                        <>
                          <ul className="max-h-56 overflow-auto p-1">
                            {productOptions.map((p) => (
                              <li key={p.id}>
                                <button
                                  onClick={() => {
                                    setSelectedProduct(p);
                                    setProductSearch("");
                                  }}
                                  className="w-full rounded-lg px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
                                >
                                  {p.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                          <div className="flex items-center justify-between border-t border-line px-2 py-1.5">
                            <button disabled={prodPage <= 1} onClick={() => getProducts(prodPage - 1, productSearch.trim())} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${prodPage <= 1 ? "text-faint" : "text-ink hover:bg-surface-2"}`}>
                              <CaretLeft size={13} /> Prev
                            </button>
                            <span className="text-xs text-muted">Page {prodPage} / {prodTotalPages}</span>
                            <button disabled={prodPage >= prodTotalPages} onClick={() => getProducts(prodPage + 1, productSearch.trim())} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${prodPage >= prodTotalPages ? "text-faint" : "text-ink hover:bg-surface-2"}`}>
                              Next <CaretRight size={13} />
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="px-3 py-2 text-sm text-muted">No products found</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button onClick={fetchStats} loading={loading}>
              Apply
            </Button>
          </div>
        </div>
      ) : (
        <div className="mb-6 flex flex-wrap items-center gap-2 text-xs text-muted">
          <span className="rounded-lg border border-line bg-surface px-2.5 py-1">{rangeSummary}</span>
          <span className="max-w-[220px] truncate rounded-lg border border-line bg-surface px-2.5 py-1">{productSummary}</span>
        </div>
      )}

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total revenue" value={currency(kpis.totalRevenue)} delta={kpis.deltas.totalRevenue} icon={CurrencyDollar} hint="vs last month" />
        <KpiCard label="Total orders" value={shortNumber(kpis.totalOrders)} delta={kpis.deltas.totalOrders} icon={ShoppingCart} hint="vs last month" />
        <KpiCard label="Avg. order value" value={currency(kpis.averageOrderValue)} delta={kpis.deltas.averageOrderValue} icon={Receipt} hint="vs last month" />
        <KpiCard label="Conversion rate" value={kpis.conversionRate == null ? "—" : pct(kpis.conversionRate)} icon={ChartLineUp} hint="orders / visitors" />
      </div>

      {/* Charts */}
      <div className="mt-4 grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <ChartCard title="Revenue over time" subtitle="Monthly gross revenue">
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c.accent} stopOpacity={0.28} />
                      <stop offset="95%" stopColor={c.accent} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke={c.line} vertical={false} />
                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={shortNumber} tick={axisTick} axisLine={false} tickLine={false} width={48} />
                  <Tooltip content={<MoneyTooltip />} cursor={{ stroke: c.line }} />
                  <Area
                    type="monotone"
                    dataKey="total"
                    name="Revenue"
                    stroke={c.accent}
                    strokeWidth={2.5}
                    fill="url(#revFill)"
                    dot={false}
                    activeDot={{ r: 4, fill: c.accent, stroke: c.surface, strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        <div className="lg:col-span-2">
          <ChartCard title="Top products" subtitle="Units sold">
            <div className="h-[320px]">
              {productData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-faint">No sales in this range</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={productData} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="4 4" stroke={c.line} horizontal={false} />
                    <XAxis type="number" tickFormatter={shortNumber} tick={axisTick} axisLine={false} tickLine={false} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={axisTick}
                      axisLine={false}
                      tickLine={false}
                      width={110}
                      tickFormatter={(v) => (v?.length > 15 ? `${v.slice(0, 14)}…` : v)}
                    />
                    <Tooltip content={<NumberTooltip />} cursor={{ fill: c.line, fillOpacity: 0.3 }} />
                    <Bar dataKey="total" name="Units" radius={[0, 6, 6, 0]} fill={c.accent} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
